import React, { useState, useEffect } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { AlertTriangle, ArrowRight, ArrowUpRight, Check, Loader2 } from 'lucide-react';

const isValidEmail = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const registerUser = async (data: {
  email: string;
  password: string;
  name: string;
  user_name: string;
}) => {
  const response = await fetch('https://api.thedressup.co/auth/register', {
    method: 'POST',
    headers: {
      accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const detail = errorData.detail;
    let message = 'Failed to sign up';
    if (typeof detail === 'string') message = detail;
    else if (Array.isArray(detail) && detail[0]?.msg) message = detail[0].msg;
    else if (detail) message = String(detail);
    throw new Error(message);
  }

  return response.json();
};

const checkUsernameAvailability = async (
  username: string
): Promise<{ available: boolean; message: string }> => {
  const response = await fetch(
    `https://api.thedressup.co/users/check-username/${encodeURIComponent(username)}`,
    { headers: { accept: 'application/json' } }
  );
  if (!response.ok) throw new Error('Failed to check username');
  return response.json();
};

const ERROR_TEXT = '#ff7a6e';
const OK_TEXT = '#7fd6a8';

const SignupSection: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [userName, setUserName] = useState('');
  const [usernameStatus, setUsernameStatus] = useState<'idle' | 'checking' | 'available' | 'unavailable'>('idle');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [signupComplete, setSignupComplete] = useState(false);
  const [registeredName, setRegisteredName] = useState('');
  const [okReady, setOkReady] = useState(false);
  const [okCountdown, setOkCountdown] = useState(3);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reduce = useReducedMotion();

  // Debounced username availability check.
  useEffect(() => {
    const trimmed = userName.trim();
    if (!trimmed) {
      setUsernameStatus('idle');
      return;
    }
    setUsernameStatus('checking');
    const timer = setTimeout(async () => {
      try {
        const result = await checkUsernameAvailability(trimmed);
        setUsernameStatus(result.available ? 'available' : 'unavailable');
      } catch {
        setUsernameStatus('idle');
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [userName]);

  useEffect(() => {
    if (!showSuccessModal) {
      setOkReady(false);
      setOkCountdown(3);
      return;
    }

    setOkReady(false);
    setOkCountdown(3);

    const countdownInterval = setInterval(() => {
      setOkCountdown((prev) => (prev > 1 ? prev - 1 : 1));
    }, 1000);

    const enableTimer = setTimeout(() => {
      setOkReady(true);
      setOkCountdown(0);
    }, 3000);

    return () => {
      clearInterval(countdownInterval);
      clearTimeout(enableTimer);
    };
  }, [showSuccessModal]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setError(null);

    if (!email.trim()) {
      setError('Please enter your email address.');
      return;
    }

    if (!isValidEmail(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    if (!password.trim()) {
      setError('Please enter a password.');
      return;
    }

    if (password.length < 8) {
      setError('Your password needs at least 8 characters.');
      return;
    }

    if (!name.trim()) {
      setError('Please enter your name.');
      return;
    }

    if (!userName.trim()) {
      setError('Please choose a username.');
      return;
    }

    if (usernameStatus === 'unavailable') {
      setError('That username is taken. Please choose another.');
      return;
    }

    if (usernameStatus === 'checking' || usernameStatus === 'idle') {
      setError('Please wait while we check your username.');
      return;
    }

    try {
      setLoading(true);
      await registerUser({
        email: email.trim(),
        password: password.trim(),
        name: name.trim(),
        user_name: userName.trim(),
      });
      setRegisteredName(name.trim());
      setSignupComplete(true);
      setShowSuccessModal(true);
      setEmail('');
      setPassword('');
      setName('');
      setUserName('');
      setUsernameStatus('idle');
    } catch (err) {
      console.error('Error signing up:', err);
      setError(err instanceof Error ? err.message : 'Sign up failed. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="signup-section" className="screen scroll-mt-16">
      <div className="shell">
      <motion.div
        initial={reduce ? false : { y: 24 }}
        whileInView={{ y: 0 }}
        viewport={{ once: true, amount: 0.15 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="panel-glass grid grid-cols-1 gap-6 px-5 py-7 sm:gap-12 sm:px-6 sm:py-14 md:px-12 md:py-20 lg:grid-cols-12 lg:gap-16 lg:px-16"
      >
        <div className="lg:col-span-5 lg:self-center">
          <h2 className="display max-w-[13ch] text-[clamp(1.75rem,7vw,3.5rem)] font-medium">
            {signupComplete
              ? `Your shop is open${registeredName ? `, ${registeredName.split(' ')[0]}` : ''}.`
              : 'Claim your shop before the doors open.'}
          </h2>

          <p className="mt-3 max-w-[40ch] text-[14px] leading-relaxed text-panel-muted sm:mt-6 sm:text-[15px]">
            {signupComplete
              ? 'Verify your email and everything is ready to browse.'
              : 'Early accounts get their palette built first and keep priority access when the marketplace opens to everyone.'}
          </p>

          {!signupComplete && (
            <ul className="mt-10 hidden border-t border-panel-line text-[14px] text-panel-muted sm:block">
              <li className="border-b border-panel-line py-3.5">Free while we are in early access</li>
              <li className="border-b border-panel-line py-3.5">No marketing email, only your account</li>
              <li className="py-3.5">Your photo is analyzed on your device</li>
            </ul>
          )}
        </div>

        <div className="lg:col-span-6 lg:col-start-7 lg:self-center">
          {signupComplete ? (
            <div className="max-w-[420px]">
              <span
                className="flex h-12 w-12 items-center justify-center"
                style={{ backgroundColor: OK_TEXT }}
              >
                <Check strokeWidth={2} className="h-6 w-6" style={{ color: '#111113' }} />
              </span>

              <p className="mt-6 text-[15px] leading-relaxed text-panel-muted">
                We sent a verification link to your inbox. If it is not there in a minute, check
                your spam or promotions folder.
              </p>

              <a
                href="https://app.thedressup.co/"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-on-panel group mt-8 w-full sm:w-auto"
              >
                Open the web app
                <ArrowUpRight
                  strokeWidth={1.5}
                  className="h-4 w-4 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                />
              </a>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate className="max-w-[520px]">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-7">
                <div>
                  <label htmlFor="signup-name" className="field-label text-panel-muted">
                    Full name
                  </label>
                  <input
                    id="signup-name"
                    name="name"
                    type="text"
                    autoComplete="name"
                    required
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      setError(null);
                    }}
                    className="field-input mt-2"
                    placeholder="Amara Osei"
                  />
                </div>

                <div>
                  <label htmlFor="signup-username" className="field-label text-panel-muted">
                    Username
                  </label>
                  <input
                    id="signup-username"
                    name="user_name"
                    type="text"
                    autoComplete="username"
                    required
                    value={userName}
                    onChange={(e) => {
                      setUserName(e.target.value);
                      setError(null);
                    }}
                    aria-describedby="username-status"
                    className="field-input mt-2"
                    placeholder="amara"
                    style={
                      usernameStatus === 'unavailable'
                        ? { borderBottomColor: ERROR_TEXT }
                        : usernameStatus === 'available'
                        ? { borderBottomColor: OK_TEXT }
                        : undefined
                    }
                  />
                  <p id="username-status" aria-live="polite" className="mt-2 h-4 text-[12px]">
                    {usernameStatus === 'checking' && (
                      <span className="text-panel-muted">Checking availability</span>
                    )}
                    {usernameStatus === 'available' && (
                      <span style={{ color: OK_TEXT }}>Available</span>
                    )}
                    {usernameStatus === 'unavailable' && (
                      <span style={{ color: ERROR_TEXT }}>Already taken</span>
                    )}
                  </p>
                </div>

                <div className="sm:col-span-2">
                  <label htmlFor="signup-email" className="field-label text-panel-muted">
                    Email
                  </label>
                  <input
                    id="signup-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setError(null);
                    }}
                    className="field-input mt-2"
                    placeholder="you@example.com"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label htmlFor="signup-password" className="field-label text-panel-muted">
                    Password
                  </label>
                  <input
                    id="signup-password"
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setError(null);
                    }}
                    aria-describedby="password-hint"
                    className="field-input mt-2"
                    placeholder="At least 8 characters"
                  />
                  <p id="password-hint" className="mt-2 hidden text-[12px] text-panel-muted sm:block">
                    Use 8 characters or more.
                  </p>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className={`btn btn-on-panel group mt-6 w-full sm:mt-9 sm:w-auto ${
                  loading ? 'cursor-not-allowed opacity-70' : ''
                }`}
              >
                {loading ? (
                  <>
                    <Loader2 strokeWidth={1.5} className="h-4 w-4 animate-spin" />
                    Creating your account
                  </>
                ) : (
                  <>
                    Create my account
                    <ArrowRight
                      strokeWidth={1.5}
                      className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
                    />
                  </>
                )}
              </button>

              {error && (
                <p
                  role="alert"
                  className="mt-5 flex items-start gap-2 text-[13px]"
                  style={{ color: ERROR_TEXT }}
                >
                  <AlertTriangle strokeWidth={1.5} className="mt-px h-4 w-4 shrink-0" />
                  {error}
                </p>
              )}
            </form>
          )}
        </div>
      </motion.div>

      {showSuccessModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-5">
          <div className="absolute inset-0 bg-ink/70 backdrop-blur-sm" aria-hidden="true" />

          <motion.div
            initial={reduce ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="signup-success-title"
            className="relative w-full max-w-lg bg-panel p-8 text-panel-ink md:p-10"
          >
            <span
              className="flex h-11 w-11 items-center justify-center"
              style={{ backgroundColor: OK_TEXT }}
            >
              <Check strokeWidth={2} className="h-6 w-6" style={{ color: '#111113' }} />
            </span>

            <h3 id="signup-success-title" className="display mt-6 text-3xl font-medium">
              Account created.
            </h3>

            <p className="mt-4 text-[15px] leading-relaxed text-panel-muted">
              We sent a verification link to your email. Open it to finish setting up your shop.
            </p>

            <div className="mt-7 border-l-2 border-panel-accent pl-5">
              <p className="text-[15px] font-semibold">Check your spam folder</p>
              <p className="mt-2 text-[14px] leading-relaxed text-panel-muted">
                Verification emails often land in Spam, Junk or Promotions. Look there before you
                request another one.
              </p>
            </div>

            <button
              type="button"
              disabled={!okReady}
              onClick={() => setShowSuccessModal(false)}
              className={`btn mt-8 w-full ${
                okReady
                  ? 'btn-on-panel'
                  : 'cursor-not-allowed border border-panel-line text-panel-muted'
              }`}
            >
              {okReady ? 'Got it' : `Got it (${okCountdown})`}
            </button>
          </motion.div>
        </div>
      )}
      </div>
    </section>
  );
};

export default SignupSection;
